import pb from '../lib/pocketbase';

export interface ReportSummary {
  id: string;
  year: number;
  month: number;
  total_invoices: number;
  total_amount: number;
  userId?: string;
}

export interface ReportsExplorerProps {
  storeId: string;
  onGroupSelect: (group: { year: number; month: number; label: string }) => void;
  onClearSelection?: () => void;
}

export class ReportsExplorer {
  private container: HTMLElement;
  private props: ReportsExplorerProps;
  private reports: ReportSummary[] = [];
  private isLoading = true;
  private error: string | null = null;
  private selectedKey: string | null = null;

  constructor(container: HTMLElement, props: ReportsExplorerProps) {
    this.container = container;
    this.props = props;
    this.init();
  }

  private async init(): Promise<void> {
    await this.loadReports();
    this.render();
  }

  private async loadReports(): Promise<void> {
    try {
      this.isLoading = true;
      this.error = null;
      this.render();

      const records = await pb.collection('reports').getFullList({
        filter: `storeId = "${this.props.storeId}"`,
        sort: '-year,-month'
      });

      this.reports = (records as unknown as ReportSummary[]).map((report) => ({
        ...report,
        total_invoices: Number(report.total_invoices ?? 0),
        total_amount: Number(report.total_amount ?? 0)
      }));

      this.isLoading = false;
      this.render();
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      this.error = 'Error al cargar los reportes. Intenta de nuevo más tarde.';
      this.isLoading = false;
      this.render();
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  private formatLabel(month: number, year: number): string {
    const paddedMonth = month.toString().padStart(2, '0');
    return `${paddedMonth}-${year}`;
  }

  private render(): void {
    if (this.isLoading) {
      this.container.innerHTML = `
        <div class="reports-explorer">
          <div class="reports-header">
            <h3>Reportes Mensuales</h3>
          </div>
          <div class="reports-loading">
            <p>⏳ Cargando reportes...</p>
          </div>
        </div>
      `;
      return;
    }

    if (this.error) {
      this.container.innerHTML = `
        <div class="reports-explorer">
          <div class="reports-header">
            <h3>Reportes Mensuales</h3>
          </div>
          <div class="reports-error">
            <p>❌ ${this.error}</p>
            <button class="reports-retry-btn">Reintentar</button>
          </div>
        </div>
      `;
      this.attachRetryListener();
      return;
    }

    if (!this.reports.length) {
      this.container.innerHTML = `
        <div class="reports-explorer">
          <div class="reports-header">
            <h3>Reportes Mensuales</h3>
          </div>
          <div class="reports-empty">
            <p>📂 Aún no hay reportes generados.</p>
            <p class="reports-empty-subtitle">Escanea facturas para construir tu historial mensual.</p>
          </div>
        </div>
      `;
      return;
    }

    const foldersHTML = this.reports
      .map((report) => {
        const label = this.formatLabel(report.month, report.year);
        const key = `${report.year}-${report.month}`;
        const isActive = this.selectedKey === key;

        return `
          <div class="report-folder${isActive ? ' report-folder-active' : ''}" data-report-key="${key}">
            <div class="report-folder-icon">📁</div>
            <div class="report-folder-info">
              <h4>${label}</h4>
              <p>${report.total_invoices} factura${report.total_invoices !== 1 ? 's' : ''}</p>
              <span class="report-folder-amount">${this.formatCurrency(report.total_amount)}</span>
            </div>
            <div class="report-folder-arrow">▶</div>
          </div>
        `;
      })
      .join('');

    this.container.innerHTML = `
      <div class="reports-explorer">
        <div class="reports-header">
          <h3>Reportes Mensuales</h3>
          ${
            this.selectedKey
              ? `<button class="reports-clear-btn" type="button">Ver todas las facturas</button>`
              : ''
          }
        </div>
        <div class="reports-grid">
          ${foldersHTML}
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachRetryListener(): void {
    const retryBtn = this.container.querySelector('.reports-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.loadReports();
      });
    }
  }

  private attachEventListeners(): void {
    const folderElements = this.container.querySelectorAll('.report-folder');
    folderElements.forEach((element) => {
      element.addEventListener('click', () => {
        const key = element.getAttribute('data-report-key');
        if (!key) return;

        if (this.selectedKey === key) {
          this.selectedKey = null;
          this.render();
          if (this.props.onClearSelection) {
            this.props.onClearSelection();
          }
          return;
        }

        const [yearStr, monthStr] = key.split('-');
        const year = Number(yearStr);
        const month = Number(monthStr);
        this.selectedKey = key;
        this.render();
        this.props.onGroupSelect({
          year,
          month,
          label: this.formatLabel(month, year)
        });
      });
    });

    const clearBtn = this.container.querySelector('.reports-clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.selectedKey = null;
        this.render();
        if (this.props.onClearSelection) {
          this.props.onClearSelection();
        }
      });
    }
  }

  public async refresh(): Promise<void> {
    await this.loadReports();
  }

  public setActiveGroup(year: number, month: number | null): void {
    if (month === null) {
      this.selectedKey = null;
    } else {
      this.selectedKey = `${year}-${month}`;
    }
    this.render();
  }

  public async updateStore(storeId: string): Promise<void> {
    if (this.props.storeId === storeId) {
      return;
    }

    this.props = {
      ...this.props,
      storeId
    };
    this.selectedKey = null;
    await this.loadReports();
  }
}


